import { type FC } from "react";

import { clsx } from "clsx";

import { faXmark } from "@fortawesome/pro-duotone-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SectionHeading } from "./section_heading";
import type { LetterCounts, LetterScope } from "./types";

type ChipTone = "indigo" | "emerald" | "amber" | "slate";

export type LetterChipDef = {
  scope: LetterScope;
  label: string;
  count: number;
  tone: ChipTone;
};

// The four letter-state chips, shared by the stats card and the records-review
// facts line. `abbreviated` shortens "Previously Reviewed" for the tight,
// single-line pane head.
export const buildLetterChips = (
  counts: LetterCounts,
  abbreviated = false,
): LetterChipDef[] => [
  { scope: "new", label: "New", count: counts.new, tone: "indigo" },
  {
    scope: "previously_reviewed",
    label: abbreviated ? "Prev. Reviewed" : "Previously Reviewed",
    count: counts.previouslyReviewed,
    tone: "emerald",
  },
  {
    scope: "non_essential",
    label: "Non-Essential",
    count: counts.nonEssential,
    tone: "amber",
  },
  {
    scope: "prior_eval",
    label: "Prior Eval",
    count: counts.priorEval,
    tone: "slate",
  },
];

const LETTER_CHIP_TONE: Record<ChipTone, { idle: string; active: string }> = {
  indigo: {
    idle: "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-300",
    active: "border-indigo-600 bg-indigo-600 text-white shadow-sm",
  },
  emerald: {
    idle: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300",
    active: "border-emerald-500 bg-emerald-500 text-white shadow-sm",
  },
  amber: {
    idle: "border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300",
    active: "border-amber-500 bg-amber-500 text-white shadow-sm",
  },
  slate: {
    idle: "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300",
    active: "border-slate-500 bg-slate-500 text-white shadow-sm",
  },
};

const ZERO_CHIP_CLASS = "border-slate-200 bg-slate-50 text-slate-400";

export const LettersSection: FC<{
  counts: LetterCounts;
  interactive: "filter" | "none";
  activeScope: LetterScope | null;
  onScopeChange?: (scope: LetterScope | null) => void;
}> = ({ counts, interactive, activeScope, onScopeChange }) => {
  const chips = buildLetterChips(counts);

  const handleClick = (scope: LetterScope) => {
    if (interactive !== "filter" || !onScopeChange) return;
    onScopeChange(activeScope === scope ? null : scope);
  };

  const showClear = interactive === "filter" && activeScope !== null;

  return (
    <div>
      <SectionHeading>Letters</SectionHeading>
      <div className="flex flex-wrap items-center gap-2">
        {chips.map((chip) => (
          <LetterChip
            key={chip.scope}
            chip={chip}
            interactive={interactive}
            active={activeScope === chip.scope}
            onClick={() => handleClick(chip.scope)}
          />
        ))}
        {showClear && (
          <button
            type="button"
            onClick={() => onScopeChange?.(null)}
            className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            aria-label="Clear filter"
          >
            <span className="inline-flex size-5 items-center justify-center rounded-full border border-slate-300">
              <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
            </span>
            <span>Clear</span>
          </button>
        )}
      </div>
    </div>
  );
};

export const LetterChip: FC<{
  chip: LetterChipDef;
  interactive: "filter" | "none";
  active: boolean;
  onClick: () => void;
}> = ({ chip, interactive, active, onClick }) => {
  const isZero = chip.count === 0;
  const isButton = interactive === "filter" && !isZero;
  const tone = LETTER_CHIP_TONE[chip.tone];
  const className = clsx(
    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
    isZero ? ZERO_CHIP_CLASS : active ? tone.active : tone.idle,
  );

  if (isButton) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(className, "cursor-pointer transition")}
        aria-pressed={active}
      >
        <strong>{chip.count}</strong> {chip.label}
      </button>
    );
  }

  return (
    <span
      className={className}
      aria-disabled={isZero || undefined}
      title={
        isZero
          ? `No ${chip.label.toLowerCase()} letters on this case.`
          : undefined
      }
    >
      <strong>{chip.count}</strong> {chip.label}
    </span>
  );
};
